import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/api-client.js";
import type { ApiSuccess } from "@energyos/shared-types";
import { AddressAutocomplete } from "@/components/AddressAutocomplete.js";
import { filterCustomers, isCustomerSelectionValid } from "./wizard-helpers.js";
import type { WizardCustomer } from "./wizard-helpers.js";

const BUILDING_TYPES = [
  { value: "residential_single", label: "Einfamilienhaus" },
  { value: "residential_multi", label: "Mehrfamilienhaus" },
  { value: "commercial", label: "Gewerbe" },
  { value: "industrial", label: "Industrie" },
  { value: "agricultural", label: "Landwirtschaft" },
  { value: "hotel", label: "Hotel" },
  { value: "open_space", label: "Freifläche" },
];

interface WizardData {
  name: string;
  customerId: string;
  address: string;
  buildingType: string;
  notes: string;
  customerName: string;
  latitude: number;
  longitude: number;
  roofTiltDeg: number;
  roofAzimuthDeg: number;
  footprintM2: string;
  // §6 Schritt 3 Verbrauch
  jahresverbrauchKwh: string;
  strompreisCt: string;
  // §6 Schritt 4 Bestand & Zukunft
  hasPv: boolean;
  hasSpeicher: boolean;
  hasWallbox: boolean;
  hasWp: boolean;
  hasNotstrom: boolean;
  plansEauto: boolean;
  plansWp: boolean;
  plansErweiterung: boolean;
  // §6 Schritt 5 Zielsetzung
  goal: string;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6;
const GOALS = [
  { value: "wirtschaftlichkeit", label: "Wirtschaftlichkeit" },
  { value: "autarkie", label: "Autarkie" },
  { value: "versorgungssicherheit", label: "Versorgungssicherheit" },
  { value: "nachhaltigkeit", label: "Nachhaltigkeit" },
  { value: "balanced", label: "Ausgewogen" },
];

export function ProjectWizard() {
  const navigate = useNavigate();
  // Übergabe aus der Kundenseite: /projects/new?customerId=… → Kunde vorbefüllen+sperren.
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get("customerId") ?? "";
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<WizardData>({
    name: "",
    customerId: "",
    address: "",
    buildingType: "residential_single",
    notes: "",
    customerName: "",
    latitude: 47.5,
    longitude: 13.0,
    roofTiltDeg: 30,
    roofAzimuthDeg: 180,
    footprintM2: "",
    jahresverbrauchKwh: "",
    strompreisCt: "",
    hasPv: false, hasSpeicher: false, hasWallbox: false, hasWp: false, hasNotstrom: false,
    plansEauto: false, plansWp: false, plansErweiterung: false,
    goal: "balanced",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Koordinaten automatisch aus der gewählten Adresse übernommen?
  const [geoFromAddress, setGeoFromAddress] = useState(false);

  // Kundenauswahl: vollständige Liste laden, Nutzer wählt EXPLIZIT
  // (kein stilles Default auf den ersten Kunden — Projekt ↔ Kunde muss stimmen).
  const [customers, setCustomers] = useState<WizardCustomer[]>([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");

  useEffect(() => {
    apiClient.get<{ items: WizardCustomer[] }>("/customers").then((res) => {
      if (res.success) {
        setCustomers(res.data.items);
        // Kommt eine customerId aus der URL und ist sie in der Liste, Kunde direkt
        // vorbefüllen (der Nutzer hat ihn auf der Kundenseite bereits gewählt).
        if (customerIdFromUrl) {
          const c = res.data.items.find((k) => k.id === customerIdFromUrl);
          // Idempotent: eine bereits getroffene (manuelle) Auswahl NICHT
          // überschreiben, falls die Kundenliste verzögert lädt.
          if (c) setForm((f) => (f.customerId ? f : { ...f, customerId: c.id, customerName: c.name, address: f.address ? f.address : (c.address ?? "") }));
        }
      }
      setCustomersLoaded(true);
    }).catch(() => { setCustomersLoaded(true); });
  }, [customerIdFromUrl]);

  const filteredCustomers = useMemo(
    () => filterCustomers(customers, customerQuery),
    [customers, customerQuery],
  );
  const customerValid = isCustomerSelectionValid(customers, form.customerId);

  const set = (field: keyof WizardData, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const setNum = (field: keyof WizardData, value: string) =>
    setForm((f) => ({ ...f, [field]: value === "" ? "" : Number(value) }));

  const setBool = (field: keyof WizardData, value: boolean) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async () => {
    if (!customerValid) {
      setError("Bitte zuerst einen Kunden auswählen (Schritt 1).");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post<{ project: { id: string } }>("/projects", {
        name: form.name,
        customerId: form.customerId,
        address: form.address,
        buildingType: form.buildingType,
        notes: form.notes,
      });
      if (!res.success) { setError(res.error.message); return; }
      const projectId = (res as ApiSuccess<{ project: { id: string } }>).data.project.id;

      // §6 Datenübernahme (Selbstläufer): Verbrauch + Zielsetzung/Bestand als Konzept-Input
      // speichern, damit Wirtschaftlichkeit/Konzept nicht neu ausgefüllt werden müssen.
      void (async () => {
        try {
          const bestand = [form.hasPv && "PV", form.hasSpeicher && "Speicher", form.hasWallbox && "Wallbox", form.hasWp && "Wärmepumpe", form.hasNotstrom && "Notstrom"].filter(Boolean).join(", ");
          const plaene = [form.plansEauto && "E-Auto", form.plansWp && "Wärmepumpe", form.plansErweiterung && "Erweiterung/Gewerbe"].filter(Boolean).join(", ");
          const inputNotes = [`Ziel: ${GOALS.find((g) => g.value === form.goal)?.label ?? form.goal}`, bestand && `Bestand: ${bestand}`, plaene && `Geplant: ${plaene}`].filter(Boolean).join(" · ").slice(0, 1000);
          await apiClient.put(`/projects/${projectId}/konzept/project-input`, {
            jahresverbrauchKwh: form.jahresverbrauchKwh !== "" ? Number(form.jahresverbrauchKwh) : null,
            strompreisCtProKwh: form.strompreisCt !== "" ? Number(form.strompreisCt) : null,
            notstromFaehig: form.hasNotstrom ? true : null,
            notes: inputNotes || null,
          });
        } catch { /* best-effort — darf den Wizard-Erfolg nicht blockieren */ }
      })();

      // fire-and-forget: Building anlegen ohne den Wizard zu blockieren
      // (apiClient stellt bereits /api voran — daher hier OHNE /api-Prefix).
      // Liefert das Building die id zurück, hängen wir best-effort eine
      // Dachfläche mit der erfassten Neigung/Ausrichtung an — sonst würden
      // roofTiltDeg/roofAzimuthDeg im Wizard still verworfen.
      void (async () => {
        try {
          const buildingRes = await apiClient.post<{ id: string }>(
            "/projects/" + projectId + "/buildings",
            {
              latitude: form.latitude,
              longitude: form.longitude,
              buildingType: form.buildingType || "residential_single",
              footprintM2: form.footprintM2 !== "" ? Number(form.footprintM2) : undefined,
            },
          );
          if (!buildingRes.success) return;
          const buildingId = buildingRes.data.id;
          if (!buildingId) return;

          // Footprint als grobe Dachfläche, sonst kleiner positiver Default (Schema verlangt areaM2 > 0).
          const areaM2 = form.footprintM2 !== "" && Number(form.footprintM2) > 0
            ? Number(form.footprintM2)
            : 30;
          // open_space/Freifläche → flach, sonst geneigtes Dach (Schema-erlaubte roofType-Werte).
          const roofType = form.buildingType === "open_space" ? "flat" : "pitched";

          await apiClient.post(
            "/projects/" + projectId + "/buildings/" + buildingId + "/roof-surfaces",
            {
              label: "Hauptdach",
              roofType,
              tiltDeg: typeof form.roofTiltDeg === "number" ? form.roofTiltDeg : 30,
              azimuthDeg: typeof form.roofAzimuthDeg === "number" ? form.roofAzimuthDeg : 180,
              areaM2,
            },
          );
        } catch {
          // best-effort — Dachfläche darf den Wizard-Erfolg nicht blockieren.
        }
      })();

      void navigate(`/projects/${projectId}`);
    } catch {
      setError("Fehler beim Anlegen des Projekts.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <div className="page-header">
        <h1 className="page-title">Neues Projekt</h1>
      </div>

      {/* Step indicator */}
      <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
        {([1, 2, 3, 4, 5, 6] as Step[]).map((s) => (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: step >= s ? "var(--color-primary)" : "var(--color-border)",
              color: step >= s ? "#fff" : "var(--color-text-muted)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 600, flexShrink: 0,
            }}>{s}</div>
            <span style={{ fontSize: 12, color: step === s ? "var(--color-primary)" : "var(--color-text-muted)", whiteSpace: "nowrap" }}>
              {(["Kunde", "Gebäude", "Verbrauch", "Bestand", "Ziel", "Details"] as const)[s - 1]}
            </span>
            {s < 6 && <span style={{ color: "var(--color-border)" }}>→</span>}
          </div>
        ))}
      </div>

      <div className="card">
        {step === 1 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 16, fontWeight: 600 }}>Kundendaten</h2>

            {customerIdFromUrl && customerValid ? (
              // Kunde wurde von der Kundenseite übergeben — gesperrt anzeigen, keine Auswahl.
              <div style={{ marginBottom: 16, padding: "10px 12px", borderRadius: 6, background: "#f0fdf4", border: "1px solid #86efac" }} data-testid="customer-locked">
                <div style={{ fontSize: 13 }}>
                  Projekt wird für <strong>{form.customerName}</strong> angelegt.
                </div>
                <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}>
                  ✓ Kunde von der Kundenseite übernommen.
                </div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label htmlFor="customer-search">Kunde suchen</label>
                  <input
                    id="customer-search"
                    data-testid="customer-search"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="Name oder E-Mail tippen, um zu filtern…"
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="customer-select">Kunde *</label>
                  <select
                    id="customer-select"
                    data-testid="customer-select"
                    value={form.customerId}
                    onChange={(e) => {
                      const c = customers.find((k) => k.id === e.target.value);
                      setForm((f) => ({ ...f, customerId: e.target.value, customerName: c?.name ?? "", address: f.address ? f.address : (c?.address ?? "") }));
                    }}
                  >
                    <option value="">— Kunde wählen —</option>
                    {filteredCustomers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.contactEmail ? ` · ${c.contactEmail}` : ""}
                      </option>
                    ))}
                  </select>
                  {customersLoaded && customers.length === 0 && (
                    <div style={{ fontSize: 12, color: "var(--color-error)", marginTop: 4 }}>
                      Noch kein Kunde vorhanden — bitte zuerst unter „Kunden" anlegen.
                    </div>
                  )}
                  {customersLoaded && customers.length > 0 && !customerValid && (
                    <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>
                      Bitte den Kunden für dieses Projekt auswählen.
                    </div>
                  )}
                  {customerValid && (
                    <div style={{ fontSize: 12, color: "#16a34a", marginTop: 4 }}>
                      ✓ Projekt wird für <strong>{form.customerName}</strong> angelegt.
                    </div>
                  )}
                </div>
              </>
            )}

            <button
              className="btn-primary"
              onClick={() => setStep(2)}
              disabled={!customerValid}
            >
              Weiter →
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 16, fontWeight: 600 }}>Gebäude & Adresse</h2>

            {/* Adresse mit Autovervollständigung — füllt Koordinaten automatisch */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="address">Adresse *</label>
              <AddressAutocomplete
                id="address"
                value={form.address}
                onChange={(v) => {
                  set("address", v);
                  setGeoFromAddress(false);
                }}
                onSelect={(p) => {
                  setForm((f) => ({ ...f, address: p.label, latitude: p.lat, longitude: p.lng }));
                  setGeoFromAddress(true);
                }}
                placeholder="Tippen, z.B. Musterstraße 1, 4020 Linz"
              />
              <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>
                Adresse eintippen und aus der Liste wählen — die Koordinaten werden
                automatisch übernommen.
              </div>
            </div>

            {/* Koordinaten — automatisch aus der Adresse, nur bei Bedarf anpassen */}
            {geoFromAddress && (
              <div style={{
                marginBottom: 12,
                fontSize: 12,
                color: "#16a34a",
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 4,
                padding: "6px 10px",
              }}>
                ✓ Koordinaten automatisch übernommen: {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}
              </div>
            )}

            {/* Zeile 1: Breitengrad + Längengrad (automatisch befüllt) */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="latitude">Breitengrad (z.B. 47.26)</label>
                <input
                  id="latitude"
                  type="number"
                  step={0.01}
                  value={form.latitude}
                  onChange={(e) => { setNum("latitude", e.target.value); setGeoFromAddress(false); }}
                  placeholder="47.5"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="longitude">Längengrad (z.B. 11.40)</label>
                <input
                  id="longitude"
                  type="number"
                  step={0.01}
                  value={form.longitude}
                  onChange={(e) => { setNum("longitude", e.target.value); setGeoFromAddress(false); }}
                  placeholder="13.0"
                />
              </div>
            </div>

            {/* Zeile 2: Gebäudetyp */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="buildingType">Gebäudetyp</label>
              <select
                id="buildingType"
                value={form.buildingType}
                onChange={(e) => set("buildingType", e.target.value)}
              >
                {BUILDING_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* Zeile 3: Dachneigung + Dachausrichtung */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="roofTiltDeg">Dachneigung (°, z.B. 30)</label>
                <input
                  id="roofTiltDeg"
                  type="number"
                  min={0}
                  max={90}
                  value={form.roofTiltDeg}
                  onChange={(e) => setNum("roofTiltDeg", e.target.value)}
                  placeholder="30"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="roofAzimuthDeg">Ausrichtung (° von Nord, 180=Süd)</label>
                <input
                  id="roofAzimuthDeg"
                  type="number"
                  min={0}
                  max={360}
                  value={form.roofAzimuthDeg}
                  onChange={(e) => setNum("roofAzimuthDeg", e.target.value)}
                  placeholder="180"
                />
              </div>
            </div>

            {/* Zeile 4: Grundfläche */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="footprintM2">Grundfläche m² (optional)</label>
              <input
                id="footprintM2"
                type="number"
                min={0}
                value={form.footprintM2}
                onChange={(e) => set("footprintM2", e.target.value)}
                placeholder="z.B. 150"
              />
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-secondary" onClick={() => setStep(1)}>← Zurück</button>
              <button className="btn-primary" onClick={() => setStep(3)} disabled={!form.address}>Weiter →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 16, fontWeight: 600 }}>Stromverbrauch</h2>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="verbrauch">Jahresstromverbrauch (kWh)</label>
              <input id="verbrauch" type="number" min={0} value={form.jahresverbrauchKwh} onChange={(e) => set("jahresverbrauchKwh", e.target.value)} placeholder="z.B. 4500" />
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>Grundlage für Wirtschaftlichkeit & Konzept — wird ins Projekt übernommen, später aus Rechnung/Smart-Meter verfeinerbar.</div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="strompreis">Strompreis (Cent/kWh, optional)</label>
              <input id="strompreis" type="number" min={0} step={0.1} value={form.strompreisCt} onChange={(e) => set("strompreisCt", e.target.value)} placeholder="z.B. 28.5" />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-secondary" onClick={() => setStep(2)}>← Zurück</button>
              <button className="btn-primary" onClick={() => setStep(4)}>Weiter →</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}>Bestand & Pläne</h2>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-muted)", margin: "8px 0" }}>Bereits vorhanden</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 16 }}>
              {([["hasPv", "PV-Anlage"], ["hasSpeicher", "Speicher"], ["hasWallbox", "Wallbox"], ["hasWp", "Wärmepumpe"], ["hasNotstrom", "Notstrom"]] as const).map(([k, l]) => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={form[k]} onChange={(e) => setBool(k, e.target.checked)} /> {l}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-muted)", margin: "8px 0" }}>Geplant</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 20 }}>
              {([["plansEauto", "E-Auto"], ["plansWp", "Wärmepumpe"], ["plansErweiterung", "Erweiterung/Gewerbe"]] as const).map(([k, l]) => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={form[k]} onChange={(e) => setBool(k, e.target.checked)} /> {l}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-secondary" onClick={() => setStep(3)}>← Zurück</button>
              <button className="btn-primary" onClick={() => setStep(5)}>Weiter →</button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 16, fontWeight: 600 }}>Zielsetzung</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {GOALS.map((g) => (
                <label key={g.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, padding: "8px 12px", border: `1px solid ${form.goal === g.value ? "var(--color-primary)" : "var(--color-border)"}`, borderRadius: 6, cursor: "pointer" }}>
                  <input type="radio" name="goal" checked={form.goal === g.value} onChange={() => set("goal", g.value)} /> {g.label}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-secondary" onClick={() => setStep(4)}>← Zurück</button>
              <button className="btn-primary" onClick={() => setStep(6)}>Weiter →</button>
            </div>
          </div>
        )}

        {step === 6 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 16, fontWeight: 600 }}>Projektdetails</h2>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="project-name">Projektname *</label>
              <input
                id="project-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="z.B. EFH Linz – PV + Speicher"
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label>Notizen</label>
              <textarea
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={3}
                placeholder="Interne Notizen zum Projekt…"
              />
            </div>
            {error && <div style={{ color: "var(--color-error)", marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-secondary" onClick={() => setStep(5)}>← Zurück</button>
              <button data-testid="wizard-submit" className="btn-primary" onClick={() => void handleSubmit()} disabled={loading || !form.name}>
                {loading ? "Wird erstellt…" : "Projekt anlegen ✓"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
